/**
 * Renewal reminders: due-date wording, urgency and month → week grouping.
 * Pure helpers so the bell, nav badges and renewal pages agree on every label.
 */

import { isLapsedBeyondRenewal, renewalPremium } from '@/utils/agentRenewal';

export const REMINDER_ATTENTION_DAYS = 30;
export const REMINDER_URGENT_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export function startOfDay(value) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Monday-start week, matching how the business plans the week. */
export function startOfWeek(value) {
  const d = startOfDay(value);
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}

export function parseRenewalDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  const text = String(value).trim();
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (isoDay) {
    return new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

export function daysUntil(value, today = new Date()) {
  const date = parseRenewalDate(value);
  if (!date) return null;
  return Math.round((date.getTime() - startOfDay(today).getTime()) / DAY_MS);
}

const shortDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const dayAndDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const monthTitle = (d) => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const plural = (n, one, many) => (n === 1 ? one : many);

export function dueText(days) {
  if (days == null) return 'Renewal date to be confirmed';
  if (days < -1) return `Cover ended ${-days} days ago`;
  if (days === -1) return 'Cover ended yesterday';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  if (days <= 30) return `Expires in ${days} days`;
  const weeks = Math.round(days / 7);
  return `Expires in ${weeks} ${plural(weeks, 'week', 'weeks')}`;
}

/** Short, positive labels used instead of "action required". */
export function urgencyOf(days) {
  if (days == null) return { key: 'ready', label: 'Ready to renew' };
  if (days < 0) return { key: 'ended', label: 'Cover ended' };
  if (days <= REMINDER_URGENT_DAYS) return { key: 'now', label: 'Renew now' };
  if (days <= REMINDER_ATTENTION_DAYS) return { key: 'soon', label: 'Renew soon' };
  return { key: 'upcoming', label: 'Coming up' };
}

export const CLOSED_URGENCY = { key: 'closed', label: 'Renewal closed' };
export const CLOSED_NUDGE = 'This cover ended more than 2 months ago, so it can no longer be renewed. '
  + 'Get a new quote to insure the vehicle again.';

export function nudgeText(days, date) {
  if (days == null || !date) return 'Review the details and renew when you are ready.';
  if (days < 0) return 'Renew now to get back on cover. The new cover starts on the day you pay.';
  if (days === 0) return 'Renew today to stay covered without a break.';
  if (days <= REMINDER_URGENT_DAYS) return `Renew by ${dayAndDate(date)} to stay covered without a break.`;
  if (days <= REMINDER_ATTENTION_DAYS) return `Renew before ${shortDate(date)} to keep your cover running.`;
  return `You can renew early. The new cover starts on ${shortDate(date)}.`;
}

export function vehicleLabel(renewal) {
  const risks = Array.isArray(renewal?.risks) ? renewal.risks : [];
  const risk = risks.find((r) => r?.riskId || r?.regNo || r?.riskDesc) || {};
  return String(renewal?.regNo || risk.regNo || risk.riskId || risk.riskDesc || '').trim();
}

export function toReminder(renewal, today = new Date()) {
  const date = parseRenewalDate(renewal?.renewalDate);
  const days = daysUntil(date, today);
  const vehicle = vehicleLabel(renewal);
  const renewable = !isLapsedBeyondRenewal(renewal, today);
  return {
    key: `${renewal?.policyNo || ''}-${renewal?.renewalDate || ''}`,
    policyNo: renewal?.policyNo || '',
    title: vehicle || renewal?.clientName || 'Motor policy',
    subtitle: vehicle && renewal?.clientName ? renewal.clientName : (renewal?.product || 'Motor'),
    date,
    dateText: date ? dayAndDate(date) : '—',
    days,
    dueText: dueText(days),
    renewable,
    nudge: renewable ? nudgeText(days, date) : CLOSED_NUDGE,
    urgency: renewable ? urgencyOf(days) : CLOSED_URGENCY,
    premium: renewalPremium(renewal),
    raw: renewal,
  };
}

/** Soonest first; undated policies last. */
export function sortReminders(reminders) {
  return [...reminders].sort((a, b) => {
    if (a.date && b.date) return a.date - b.date;
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });
}

function weekTitle(weekStart, today) {
  const thisWeek = startOfWeek(today).getTime();
  const diffWeeks = Math.round((weekStart.getTime() - thisWeek) / (7 * DAY_MS));
  if (diffWeeks === 0) return 'This week';
  if (diffWeeks === 1) return 'Next week';
  if (diffWeeks === -1) return 'Last week';
  return `Week of ${shortDate(weekStart)}`;
}

function weekRange(weekStart, monthDate) {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const from = weekStart < monthStart ? monthStart : weekStart;
  const to = weekEnd > monthEnd ? monthEnd : weekEnd;
  if (from.getTime() === to.getTime()) return shortDate(from);
  return `${from.getDate()} – ${shortDate(to)}`;
}

/**
 * Lapsed first, then calendar months (soonest first), each split into Monday-start weeks.
 * @returns {{ ended: object[], months: object[], undated: object[] }}
 */
export function groupReminders(reminders, today = new Date()) {
  const sorted = sortReminders(reminders);
  const ended = sorted.filter((r) => r.days != null && r.days < 0);
  const undated = sorted.filter((r) => r.days == null);
  const months = [];
  const thisMonthKey = `${today.getFullYear()}-${today.getMonth()}`;

  sorted.filter((r) => r.days != null && r.days >= 0).forEach((r) => {
    const monthKey = `${r.date.getFullYear()}-${r.date.getMonth()}`;
    let month = months.find((m) => m.key === monthKey);
    if (!month) {
      month = {
        key: monthKey,
        title: monthTitle(r.date),
        isCurrent: monthKey === thisMonthKey,
        count: 0,
        weeks: [],
      };
      months.push(month);
    }
    const weekStart = startOfWeek(r.date);
    const weekKey = `${monthKey}-${weekStart.getTime()}`;
    let week = month.weeks.find((w) => w.key === weekKey);
    if (!week) {
      week = {
        key: weekKey,
        title: weekTitle(weekStart, today),
        range: weekRange(weekStart, r.date),
        items: [],
      };
      month.weeks.push(week);
    }
    week.items.push(r);
    month.count += 1;
  });

  return { ended, months, undated };
}

export function reminderSummary(reminders) {
  const dated = reminders.filter((r) => r.days != null);
  return {
    total: reminders.length,
    ended: dated.filter((r) => r.days < 0).length,
    urgent: dated.filter((r) => r.days >= 0 && r.days <= REMINDER_URGENT_DAYS).length,
    soon: dated.filter((r) => r.days > REMINDER_URGENT_DAYS && r.days <= REMINDER_ATTENTION_DAYS).length,
    later: dated.filter((r) => r.days > REMINDER_ATTENTION_DAYS).length,
  };
}

/** One friendly sentence for the top of the reminders panel. */
export function reminderHeadline(summary) {
  const policies = (n) => `${n} ${plural(n, 'policy', 'policies')}`;
  if (!summary.total) {
    return {
      title: 'You are all caught up',
      body: 'No motor policies are due for renewal in the next 90 days.',
    };
  }
  if (summary.ended) {
    return {
      title: 'Needs your attention',
      body: `${policies(summary.ended)} ${plural(summary.ended, 'has', 'have')} lapsed. Renew now to get back on cover from the day you pay.`,
    };
  }
  if (summary.urgent) {
    return {
      title: 'Needs your attention',
      body: `${policies(summary.urgent)} ${plural(summary.urgent, 'expires', 'expire')} within 7 days. Renew early to stay covered without a break.`,
    };
  }
  if (summary.soon) {
    return {
      title: 'Coming up this month',
      body: `${policies(summary.soon)} ${plural(summary.soon, 'is', 'are')} due in the next 30 days.`,
    };
  }
  return {
    title: 'Plan ahead',
    body: `${policies(summary.total)} ${plural(summary.total, 'is', 'are')} coming up for renewal. You can renew any time.`,
  };
}

export function formatBadgeCount(count) {
  if (!count) return '';
  return count > 99 ? '99+' : String(count);
}
